import { Colors, Typography } from "@factorearth/component-library";
import { Box } from "@factorearth/component-library/dist/Atoms/Box/Box";
import CreateNewProject from "components/CreateNewProject/CreateNewProject";
import ProjectList from "components/ProjectList/ProjectList";
import { FormTemplateForResolver, Project, ProjectForResolver, UserForResolver } from "db/definitions";
import React, { useState } from "react";
import { RecentProject, updateRecentProjects } from "utils/localstorage/projects";
import { useNavigate } from "react-router";
import styles from "./Home.module.scss";
import { FiChevronsRight } from "react-icons/fi";
import RestoreProjectFromJson from "components/RestoreProjectFromJson/RestoreProjectFromJson";

interface HomeProps {
	colorPalette: Colors;
	createProject: (proj: Pick<ProjectForResolver, "name" | "description">) => Promise<Project | undefined>;
	getProjects: () => Promise<void>;
	handleChangeProject: (obj: { target: { label?: string; value: string } }) => void;
	online: boolean;
	projectId: string;
	projects: ProjectForResolver[];
	recentProjects: RecentProject[];
	userDoc: UserForResolver | null | undefined;
	formTemplates: FormTemplateForResolver[];
}

const Home = (props: HomeProps) => {
	const { colorPalette, createProject, getProjects, handleChangeProject, online, projectId, projects, recentProjects, userDoc, formTemplates } = props;
	const navigate = useNavigate();
	const [createProjectOpen, setCreateProjectOpen] = useState<boolean>(false);
	const [restoreProjectJsonOpen, setRestoreProjectJsonOpen] = useState<boolean>(false);

	const selectRecentProject = (recentProject: RecentProject) => {
		const changeData = {
			target: {
				value: recentProject.id,
				label: recentProject.name,
			},
		};

		handleChangeProject(changeData);
		navigate(`/project/${recentProject.id}`);
		updateRecentProjects(recentProject);
	};

	function navigateToAllProjects() {
		navigate("/all-projects");
	}

	return (
		<div data-test="landing-header" className={styles.landingHeader}>
			<div className={styles.homeContainer}>
				<div className={styles.welcomeText}>
					<Typography content="Welcome to Record™" variant="Heading 1" textColor={colorPalette.text.primary} />
					<Typography content="Select or create a project" variant="Paragraphy Body" textColor={colorPalette.text.primary} />
				</div>
				{recentProjects.length > 0 && (
					<>
						<Typography content="Recent Projects" variant="Heading 5" textColor={colorPalette.text.primary} />
						<div className={styles.homeSection} data-test="recent-projects">
							{recentProjects.map((project) => (
								<Box
									key={project.id}
									id={project.id}
									data-test={project.name}
									colorPalette={colorPalette}
									onClick={() => selectRecentProject(project)}
									style={{
										maxWidth: "240px",
										overflowWrap: "anywhere",
									}}>
									<Typography
										data-test={`recent-project-name-${project.id}`}
										content={project.name}
										variant="Heading 4"
										textColor={colorPalette.text.primary}
									/>
									{project.description && (
										<Typography
											data-test={`recent-project-description-${project.id}`}
											content={project.description}
											variant="Small"
											textColor={colorPalette.text.primary}
										/>
									)}
								</Box>
							))}
						</div>
					</>
				)}
				<Typography variant="Heading 5" textColor={colorPalette.text.tertiary} onClick={navigateToAllProjects} className={styles.allProjectsButton}>
					View all projects <FiChevronsRight />
				</Typography>
				<Typography content="Create a New Project or Restore a Project" variant="Heading 5" textColor={colorPalette.text.primary} />
				<div className={styles.homeSection}>
					{userDoc && (
						<Box
							colorPalette={colorPalette}
							style={{
								maxWidth: "240px",
							}}
							onClick={() => setCreateProjectOpen(true)}>
							<Typography
								data-test="create-new-project"
								content="Create a New Project"
								variant="Heading 4"
								textColor={colorPalette.text.primary}
							/>
						</Box>
					)}
					<RestoreProjectFromJson
						colors={colorPalette}
						getProjects={getProjects}
						online={online}
						open={restoreProjectJsonOpen}
						projects={projects}
						setOpen={setRestoreProjectJsonOpen}
						showButton={true}
					/>
					{userDoc && createProjectOpen && (
						<CreateNewProject
							group={userDoc.group}
							handleCreateProject={createProject}
							projects={projects}
							formTemplates={formTemplates}
							online={online}
							open={createProjectOpen}
							setOpen={setCreateProjectOpen}
						/>
					)}
				</div>
				<Typography content="Find what you are looking for..." variant="Heading 5" textColor={colorPalette.text.primary} />
				<div
					style={{
						width: "100%",
						display: "flex",
						justifyContent: "center",
					}}>
					<Box colorPalette={colorPalette} className={styles.projectListBox}>
						<div
							style={{
								alignSelf: "stretch",
								fontSize: "20px",
								fontStyle: "normal",
								fontWeight: "700",
								lineHeight: "25px",
							}}>
							<ProjectList
								color={colorPalette}
								projectId={projectId}
								getProjects={getProjects}
								handleChangeProject={handleChangeProject}
								online={online}
								projects={projects as Project[]}
								recentProjects={recentProjects}
								header={<Typography content="Search for a Project" variant="Heading 4" textColor={colorPalette.text.primary} />}
								setCreateProjectOpen={setCreateProjectOpen}
							/>
						</div>
					</Box>
				</div>
			</div>
		</div>
	);
};

export default Home;
